import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase/config';

const DEFAULT_PHP_REGISTER_URL = 'http://localhost/api/register_card.php';
const DEFAULT_DEVICE_IP = '192.168.0.115';

interface StudentOption {
    id: string;
    name: string;
    admissionNo?: string;
}

interface ActivityLog {
    id: string;
    type: string;
    status: string;
    message: string;
    uid?: string;
    studentDocId?: string;
    createdAtLabel: string;
}

const DeviceConfiguration: React.FC = () => {
    const [students, setStudents] = useState<StudentOption[]>([]);
    const [isLoadingStudents, setIsLoadingStudents] = useState(true);
    const [studentSearch, setStudentSearch] = useState('');
    const [selectedStudentDocId, setSelectedStudentDocId] = useState('');
    const [phpRegisterUrl, setPhpRegisterUrl] = useState<string>(DEFAULT_PHP_REGISTER_URL);
    const [deviceIp, setDeviceIp] = useState<string>(DEFAULT_DEVICE_IP);
    const [isRegistering, setIsRegistering] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [lastScannedUID, setLastScannedUID] = useState<string | null>(null);
    const [registrationStatus, setRegistrationStatus] = useState<'success' | 'failed' | null>(null);
    const [registrationMessage, setRegistrationMessage] = useState<string>('');
    const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
    const [isLoadingLogs, setIsLoadingLogs] = useState(true);

    useEffect(() => {
        const fetchStudents = async () => {
            try {
                const studentsRef = collection(db, 'students');
                const studentsQuery = query(studentsRef, orderBy('createdAt', 'desc'));
                const snapshot = await getDocs(studentsQuery);

                const data: StudentOption[] = snapshot.docs.map((studentDoc) => {
                    const payload = studentDoc.data() as any;
                    const firstName = payload.firstName || '';
                    const lastName = payload.lastName || '';
                    const fallbackName = payload.fullName || payload.name || payload.studentName || 'Unnamed Student';

                    return {
                        id: studentDoc.id,
                        name: `${firstName} ${lastName}`.trim() || fallbackName,
                        admissionNo: payload.academicInfo?.admissionNo || payload.studentId || payload.registrationNumber,
                    };
                });

                setStudents(data);
            } catch (error) {
                console.error('Error loading students:', error);
            } finally {
                setIsLoadingStudents(false);
            }
        };

        fetchStudents();
    }, []);

    useEffect(() => {
        const logsRef = collection(db, 'activity_logs');
        const logsQuery = query(logsRef, orderBy('createdAt', 'desc'), limit(40));
        const unsubscribe = onSnapshot(
            logsQuery,
            (snapshot) => {
                const mapped: ActivityLog[] = snapshot.docs.map((logDoc) => {
                    const payload = logDoc.data() as any;
                    const createdAtDate = payload?.createdAt?.toDate ? payload.createdAt.toDate() : new Date();
                    return {
                        id: logDoc.id,
                        type: payload?.type || 'unknown',
                        status: payload?.status || 'info',
                        message: payload?.message || 'No message',
                        uid: payload?.uid || undefined,
                        studentDocId: payload?.studentDocId || undefined,
                        createdAtLabel: createdAtDate.toLocaleString(),
                    };
                });
                setActivityLogs(mapped);
                setIsLoadingLogs(false);
            },
            (error) => {
                console.error('Failed to stream activity logs:', error);
                setIsLoadingLogs(false);
            }
        );

        return () => unsubscribe();
    }, []);

    const filteredStudents = useMemo(() => {
        const keyword = studentSearch.trim().toLowerCase();
        if (!keyword) return students;

        return students.filter((student) =>
            student.name.toLowerCase().includes(keyword) ||
            (student.admissionNo || '').toLowerCase().includes(keyword) ||
            student.id.toLowerCase().includes(keyword)
        );
    }, [studentSearch, students]);

    const registerCard = async () => {
        if (!selectedStudentDocId) {
            return;
        }

        setIsRegistering(true);
        setRegistrationStatus(null);
        setRegistrationMessage('Preparing device for scan...');

        try {
            const endpointUrl = new URL(phpRegisterUrl);
            endpointUrl.searchParams.set('studentDocId', selectedStudentDocId);
            endpointUrl.searchParams.set('deviceIP', deviceIp.trim());
            endpointUrl.searchParams.set('prepareOnly', '1');
            const registrationUrl = endpointUrl.toString();
            const phpResponse = await fetch(registrationUrl);
            const rawText = await phpResponse.text();

            let phpData: any = null;
            try {
                phpData = JSON.parse(rawText);
            } catch {
                setRegistrationStatus('failed');
                setRegistrationMessage(`PHP did not return JSON. First response: ${rawText.slice(0, 120)}`);
                return;
            }

            setRegistrationStatus(phpData.success ? 'success' : 'failed');
            setRegistrationMessage(phpData.message || 'No message returned from PHP endpoint.');
            if (phpData.rfidUID) {
                setLastScannedUID(phpData.rfidUID);
            }
        } catch (error) {
            console.error('Failed to set student for registration:', error);
            setRegistrationStatus('failed');
            setRegistrationMessage('Could not prepare device. Check device IP and PHP endpoint.');
        } finally {
            setIsRegistering(false);
        }
    };

    const checkRegistration = async () => {
        if (!selectedStudentDocId) {
            return;
        }

        setIsChecking(true);
        setRegistrationStatus(null);
        setRegistrationMessage('Checking student card in database...');

        try {
            const studentRef = doc(db, 'students', selectedStudentDocId);
            const studentSnap = await getDoc(studentRef);
            if (!studentSnap.exists()) {
                setRegistrationStatus('failed');
                setRegistrationMessage('Student record not found.');
                return;
            }

            const studentData = studentSnap.data() as any;
            const uid = studentData?.rfidUID;
            if (uid) {
                setLastScannedUID(uid);
                setRegistrationStatus('success');
                setRegistrationMessage(`Card saved successfully. UID: ${uid}`);
                return;
            }

            setRegistrationStatus('failed');
            setRegistrationMessage('Card not saved yet. Please scan card, then press Check again.');
        } catch (error) {
            console.error('Failed to check card registration:', error);
            setRegistrationStatus('failed');
            setRegistrationMessage('Failed to check database. Try again.');
        } finally {
            setIsChecking(false);
        }
    };

    const canRegister = !!selectedStudentDocId && !isRegistering && !isChecking;
    const canCheck = !!selectedStudentDocId && !isRegistering && !isChecking;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">Device Configuration</h1>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

                <section className="card p-6 space-y-4 xl:col-span-1">
                    <h2 className="text-lg font-semibold text-gray-900">Student Selection</h2>

                    <input
                        type="text"
                        value={studentSearch}
                        onChange={(e) => setStudentSearch(e.target.value)}
                        placeholder="Search by name/admission no..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">PHP Endpoint URL</label>
                        <input
                            type="text"
                            value={phpRegisterUrl}
                            onChange={(e) => setPhpRegisterUrl(e.target.value)}
                            placeholder="http://localhost/api/register_card.php"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">ESP32 Device IP</label>
                        <input
                            type="text"
                            value={deviceIp}
                            onChange={(e) => setDeviceIp(e.target.value)}
                            placeholder="192.168.100.9"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                    </div>

                    <select
                        value={selectedStudentDocId}
                        onChange={(e) => setSelectedStudentDocId(e.target.value)}
                        disabled={isLoadingStudents}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100"
                    >
                        <option value="">{isLoadingStudents ? 'Loading students...' : 'Select a student'}</option>
                        {filteredStudents.map((student) => (
                            <option key={student.id} value={student.id}>
                                {student.name}{student.admissionNo ? ` (${student.admissionNo})` : ''}
                            </option>
                        ))}
                    </select>

                    <button
                        type="button"
                        onClick={registerCard}
                        disabled={!canRegister}
                        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isRegistering ? 'Triggering register mode...' : 'Trigger Register Mode'}
                    </button>

                    <button
                        type="button"
                        onClick={checkRegistration}
                        disabled={!canCheck}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isChecking ? 'Checking...' : 'Check Saved Card'}
                    </button>
                </section>

                <section className="card p-6 space-y-4 xl:col-span-1">
                    <h2 className="text-lg font-semibold text-gray-900">Card Scan Feedback</h2>

                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <p className="text-sm text-gray-600">Scanned UID</p>
                        <p className="font-mono text-base text-gray-900">{lastScannedUID || 'No scan detected yet'}</p>
                    </div>

                    <div className={`rounded-lg p-4 ${registrationStatus === 'success'
                            ? 'bg-green-50 text-green-700'
                            : registrationStatus === 'failed'
                                ? 'bg-red-50 text-red-700'
                                : 'bg-gray-50 text-gray-700'
                        }`}
                    >
                        <p className="font-medium">
                            {registrationStatus === 'success'
                                ? 'Registration Status: Success'
                                : registrationStatus === 'failed'
                                    ? 'Registration Status: Failed'
                                    : 'Registration Status: Waiting'}
                        </p>
                        <p className="text-sm mt-1">{registrationMessage || 'Click Register Card, scan card on device, then click Check Saved Card.'}</p>
                    </div>

                    <div className="rounded-lg border border-gray-200">
                        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                            <p className="font-medium text-gray-900">Activity Stream (Firestore)</p>
                            <p className="text-xs text-gray-600">Registration + attendance logs (live stream)</p>
                        </div>
                        <div className="max-h-80 overflow-auto">
                            {isLoadingLogs ? (
                                <p className="px-4 py-3 text-sm text-gray-600">Loading logs...</p>
                            ) : activityLogs.length === 0 ? (
                                <p className="px-4 py-3 text-sm text-gray-600">No activity yet.</p>
                            ) : (
                                activityLogs.map((log) => (
                                    <div key={log.id} className="px-4 py-3 border-b border-gray-100 last:border-b-0">
                                        <p className="text-sm text-gray-900">
                                            <span className="font-semibold uppercase">{log.type}</span>
                                            {' '}
                                            <span className={log.status === 'success' ? 'text-green-700' : log.status === 'failed' ? 'text-red-700' : 'text-gray-700'}>
                                                ({log.status})
                                            </span>
                                            : {log.message}
                                        </p>
                                        <p className="text-xs text-gray-600 mt-1">
                                            {log.createdAtLabel}
                                            {log.uid ? ` | UID: ${log.uid}` : ''}
                                            {log.studentDocId ? ` | Student: ${log.studentDocId}` : ''}
                                        </p>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default DeviceConfiguration;
